import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  FormControlLabel,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  projectEssentialApi,
  type FilterOptions,
  type ProjectFilters,
  type UpcomingProject,
} from '../services/api';

interface ProjectFiltersDialogProps {
  open: boolean;
  onClose: () => void;
  /** Filters currently applied on the dashboard. */
  value: ProjectFilters;
  /** Called when the user taps Apply. */
  onApply: (next: ProjectFilters) => void;
  /**
   * Hide the Property Type field. Used on pages that pre-scope the list
   * to a specific property bucket (e.g. the Commercial & Industrial
   * listing page), where the user shouldn't be able to override it.
   */
  hidePropertyType?: boolean;
  /**
   * Property-type bucket ids to omit from the Property Type dropdown
   * (e.g. `commercial` / `local` / `international` on All Projects,
   * which is already scoped to SG residential).
   */
  excludePropertyTypes?: string[];
}

type DraftProjectFilters = Required<Omit<ProjectFilters, 'soleMarketing'>> & {
  soleMarketing: boolean;
};

const EMPTY_FILTERS: DraftProjectFilters = {
  regions: [],
  districts: [],
  propertyTypes: [],
  projectIds: [],
  soleMarketing: false,
};

const EMPTY_EXCLUDED_PROPERTY_TYPES: string[] = [];

function arraysEqual(a: string[] = [], b: string[] = []): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

// Format a raw `DistrictNumber` from the DB into the canonical `D01` /
// `D09` / `D27` form expected by the UI. The DB stores values inconsistently
// (e.g. `1`, `01`, `D1`, `D01`), so we strip any leading `D`s and pad the
// numeric portion to 2 digits before re-prefixing exactly once.
function formatDistrictNo(no: string): string {
  const cleaned = no.replace(/^D+/i, '').trim();
  // If the remainder isn't purely numeric, fall back to whatever the DB had,
  // just normalized to a single `D` prefix.
  if (!/^\d+$/.test(cleaned)) return `D${cleaned}`;
  return `D${cleaned.padStart(2, '0')}`;
}

// Friendly district label. The `District.Name` column already starts with the
// formatted code (e.g. `D01 - City Hall`), so we render it as-is when present
// and only fall back to the synthesized `D01` form when no name is available.
function formatDistrictLabel(
  no: string,
  name: string | null | undefined,
): string {
  if (name && name !== `District ${no}`) return name;
  return formatDistrictNo(no);
}

// Section caption above each Autocomplete — mirrors the look used by
// `renderCaption` in `ProjectResearchPage` so the dropdowns feel consistent.
function FieldCaption({ label, count }: { label: string; count: number }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mb: 0.5,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.72rem',
        color: 'var(--color-text-strong)',
      }}
    >
      {label}
      {count > 0 && (
        <Box component="span" sx={{ ml: 0.75, color: 'var(--color-accent-strong)' }}>
          ({count})
        </Box>
      )}
    </Typography>
  );
}

export default function ProjectFiltersDialog({
  open,
  onClose,
  value,
  onApply,
  hidePropertyType = false,
  excludePropertyTypes = EMPTY_EXCLUDED_PROPERTY_TYPES,
}: ProjectFiltersDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [options, setOptions] = React.useState<FilterOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = React.useState(false);
  const [optionsError, setOptionsError] = React.useState<string | null>(null);

  const [projects, setProjects] = React.useState<UpcomingProject[]>([]);
  const [projectsLoading, setProjectsLoading] = React.useState(false);
  const [projectsError, setProjectsError] = React.useState<string | null>(null);

  // Local working copy of filters, only committed when the user taps Apply.
  const [draft, setDraft] = React.useState<DraftProjectFilters>(EMPTY_FILTERS);

  const excludedPropertyTypeSet = React.useMemo(
    () => new Set(excludePropertyTypes),
    [excludePropertyTypes],
  );

  const propertyTypeOptions = React.useMemo(
    () =>
      (options?.propertyTypes ?? []).filter(
        (p) => !excludedPropertyTypeSet.has(p.id),
      ),
    [options, excludedPropertyTypeSet],
  );

  const projectById = React.useMemo(() => {
    const map = new Map<string, UpcomingProject>();
    for (const p of projects) map.set(p.id, p);
    return map;
  }, [projects]);

  const hasProjectSelection = draft.projectIds.length > 0;
  const hasDetailSelection =
    draft.regions.length > 0 ||
    draft.districts.length > 0 ||
    draft.propertyTypes.length > 0;

  // Reset draft to incoming value whenever the dialog opens. Drop any
  // selections that aren't offered for this page (excluded buckets).
  // Enforce mutual exclusivity: prefer explicit projectIds when both
  // somehow exist on the applied value.
  React.useEffect(() => {
    if (!open) return;
    const projectIds = value.projectIds ?? [];
    const detailRegions = value.regions ?? [];
    const detailDistricts = value.districts ?? [];
    const detailPropertyTypes = (value.propertyTypes ?? []).filter(
      (id) => !excludedPropertyTypeSet.has(id),
    );
    const soleMarketing = value.soleMarketing === true;
    if (projectIds.length > 0) {
      setDraft({
        regions: [],
        districts: [],
        propertyTypes: [],
        projectIds,
        soleMarketing,
      });
    } else {
      setDraft({
        regions: detailRegions,
        districts: detailDistricts,
        propertyTypes: detailPropertyTypes,
        projectIds: [],
        soleMarketing,
      });
    }
  }, [open, value, excludedPropertyTypeSet]);

  // Lazy-load options the first time the dialog is opened. We deliberately
  // omit `optionsLoading` from the dependency list: including it would cause
  // the effect to re-run after we set it to true, invalidate the in-flight
  // request via the cleanup (`mounted = false`), and leave the spinner stuck.
  React.useEffect(() => {
    if (!open || options) return;
    let mounted = true;
    setOptionsLoading(true);
    setOptionsError(null);
    projectEssentialApi
      .getFilterOptions()
      .then((res) => {
        if (mounted) setOptions(res);
      })
      .catch((err) => {
        if (mounted) setOptionsError(err?.message || 'Failed to load filter options');
      })
      .finally(() => {
        if (mounted) setOptionsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [open, options]);

  // Resi + commercial + international listing pools for the Project
  // multi-select (same scopes as the respective listing pages).
  React.useEffect(() => {
    if (!open || projects.length > 0) return;
    let mounted = true;
    setProjectsLoading(true);
    setProjectsError(null);
    projectEssentialApi
      .getFilterProjectPool()
      .then((items) => {
        if (mounted) setProjects(items);
      })
      .catch((err) => {
        if (mounted) {
          setProjectsError(err?.message || 'Failed to load projects');
        }
      })
      .finally(() => {
        if (mounted) setProjectsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [open, projects.length]);

  const dirty =
    !arraysEqual(draft.regions, value.regions ?? []) ||
    !arraysEqual(draft.districts, value.districts ?? []) ||
    !arraysEqual(draft.propertyTypes, value.propertyTypes ?? []) ||
    !arraysEqual(draft.projectIds, value.projectIds ?? []) ||
    draft.soleMarketing !== (value.soleMarketing === true);

  const totalActive =
    draft.regions.length +
    draft.districts.length +
    draft.propertyTypes.length +
    draft.projectIds.length +
    (draft.soleMarketing ? 1 : 0);

  const handleClearAll = () => {
    setDraft(EMPTY_FILTERS);
  };

  const handleApply = () => {
    const soleMarketing = draft.soleMarketing ? true : undefined;
    if (draft.projectIds.length > 0) {
      onApply({
        regions: [],
        districts: [],
        propertyTypes: [],
        projectIds: draft.projectIds,
        soleMarketing,
      });
    } else {
      onApply({
        regions: draft.regions,
        districts: draft.districts,
        propertyTypes: draft.propertyTypes,
        projectIds: [],
        soleMarketing,
      });
    }
    onClose();
  };

  const setProjectIds = (projectIds: string[]) => {
    setDraft((d) => ({
      regions: [],
      districts: [],
      propertyTypes: [],
      projectIds,
      soleMarketing: d.soleMarketing,
    }));
  };

  const setDetailField = (
    patch: Partial<Pick<DraftProjectFilters, 'regions' | 'districts' | 'propertyTypes'>>,
  ) => {
    setDraft((d) => ({
      ...d,
      ...patch,
      projectIds: [],
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface-elevated-strong)',
          color: 'var(--color-text)',
          backgroundImage: 'none',
          border: { sm: '1px solid var(--color-border)' },
          borderRadius: { xs: 0, sm: 2 },
          // Wide on tablet/desktop, full screen on phones. Height grows to fit
          // content (capped) so the dialog doesn't feel cavernous on desktop.
          width: { xs: '100%', sm: '88vw' },
          height: { xs: '100%', sm: 'auto' },
          maxWidth: { sm: '1100px' },
          maxHeight: { sm: '88vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 600,
              fontSize: '0.7rem',
              display: 'block',
            }}
          >
            Refine Search based on Project details
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'var(--color-text-strong)',
              fontSize: { xs: '1rem', sm: '1.15rem' },
              lineHeight: 1.2,
            }}
          >
            Filters{totalActive > 0 ? ` (${totalActive})` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close filters" sx={{ color: 'var(--color-text-strong)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
        {(optionsLoading && !options) || (projectsLoading && projects.length === 0) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : optionsError ? (
          <Typography color="error" sx={{ py: 2 }}>
            {optionsError}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FieldCaption label="Project" count={draft.projectIds.length} />
                {projectsError ? (
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {projectsError}
                  </Typography>
                ) : null}
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  disabled={hasDetailSelection}
                  options={projects.map((p) => p.id)}
                  value={draft.projectIds}
                  onChange={(_e, next) => setProjectIds(next as string[])}
                  filterOptions={(ids, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) return ids;
                    return ids.filter((id) => {
                      const name = (projectById.get(id)?.name || '').toLowerCase();
                      return name.includes(q);
                    });
                  }}
                  getOptionLabel={(id) => projectById.get(id)?.name || id}
                  noOptionsText="No matching projects"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoComplete="off"
                      inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                      placeholder={
                        hasDetailSelection
                          ? 'Clear region / district / property type to filter by project'
                          : draft.projectIds.length
                            ? ''
                            : 'Search and select projects'
                      }
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' } }}
                    />
                  )}
                  renderTags={(selected, getTagProps) =>
                    selected.map((id, idx) => (
                      <Chip
                        {...getTagProps({ index: idx })}
                        key={id}
                        label={projectById.get(id)?.name || id}
                        size="small"
                      />
                    ))
                  }
                />
            </Grid>

            {/* Region */}
            <Grid item xs={12} sm={6} md={4}>
              <FieldCaption label="Region" count={draft.regions.length} />
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                disabled={hasProjectSelection}
                options={options?.regions ?? []}
                value={draft.regions}
                onChange={(_e, next) => setDetailField({ regions: next as string[] })}
                noOptionsText="No regions available"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                    placeholder={
                      hasProjectSelection
                        ? 'Clear projects to filter by region'
                        : draft.regions.length
                          ? ''
                          : 'Select regions'
                    }
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' } }}
                  />
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((opt, idx) => (
                    <Chip {...getTagProps({ index: idx })} key={opt} label={opt} size="small" />
                  ))
                }
              />
            </Grid>

            {/* Districts — friendly label "D9 · Orchard". Grouped by region. */}
            <Grid item xs={12} sm={6} md={4}>
              <FieldCaption label="District" count={draft.districts.length} />
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                disabled={hasProjectSelection}
                options={(options?.districts ?? []).map((d) => d.no)}
                value={draft.districts}
                onChange={(_e, next) => setDetailField({ districts: next as string[] })}
                groupBy={(no) => {
                  const meta = options?.districts.find((d) => d.no === no);
                  return meta?.region || 'Other';
                }}
                getOptionLabel={(no) => {
                  const meta = options?.districts.find((d) => d.no === no);
                  return formatDistrictLabel(no, meta?.name);
                }}
                noOptionsText="No districts available"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                    placeholder={
                      hasProjectSelection
                        ? 'Clear projects to filter by district'
                        : draft.districts.length
                          ? ''
                          : 'Select districts'
                    }
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' } }}
                  />
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((opt, idx) => {
                    const meta = options?.districts.find((d) => d.no === opt);
                    return (
                      <Chip
                        {...getTagProps({ index: idx })}
                        key={opt}
                        label={formatDistrictLabel(opt, meta?.name)}
                        size="small"
                      />
                    );
                  })
                }
              />
            </Grid>

            {/* Property type buckets — hidden on pages that pre-scope
                the list to a fixed property bucket. */}
            {!hidePropertyType && (
            <Grid item xs={12} sm={6} md={4}>
              <FieldCaption label="Property Type" count={draft.propertyTypes.length} />
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                disabled={hasProjectSelection}
                options={propertyTypeOptions.map((p) => p.id)}
                value={draft.propertyTypes}
                onChange={(_e, next) =>
                  setDetailField({ propertyTypes: next as string[] })
                }
                getOptionLabel={(id) =>
                  propertyTypeOptions.find((p) => p.id === id)?.label ??
                  options?.propertyTypes.find((p) => p.id === id)?.label ??
                  id
                }
                noOptionsText="No property types available"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                    placeholder={
                      hasProjectSelection
                        ? 'Clear projects to filter by property type'
                        : draft.propertyTypes.length
                          ? ''
                          : 'Select property types'
                    }
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' } }}
                  />
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((opt, idx) => {
                    const label =
                      propertyTypeOptions.find((p) => p.id === opt)?.label ??
                      options?.propertyTypes.find((p) => p.id === opt)?.label ??
                      opt;
                    return (
                      <Chip {...getTagProps({ index: idx })} key={opt} label={label} size="small" />
                    );
                  })
                }
              />
            </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.soleMarketing}
                    onChange={(_, checked) =>
                      setDraft((d) => ({ ...d, soleMarketing: checked }))
                    }
                    sx={{ color: 'var(--color-text-muted)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>
                    Sole Marketing
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderTop: '1px solid var(--color-border)',
          gap: 1,
        }}
      >
        <Button
          onClick={handleClearAll}
          disabled={totalActive === 0}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: 'var(--color-text-muted)',
            '&:hover': { color: 'var(--color-text-strong)', bgcolor: 'transparent' },
          }}
        >
          Clear all
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: 'var(--color-text-strong)',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          disabled={!dirty}
          variant="contained"
          disableElevation
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999,
            px: 3,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            '&:hover': { bgcolor: 'var(--color-accent-strong)' },
            '&.Mui-disabled': {
              bgcolor: 'var(--color-surface-elevated)',
              color: 'var(--color-text-muted)',
            },
          }}
        >
          Apply{totalActive > 0 ? ` (${totalActive})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
